import React from 'react';
import { createPortal } from 'react-dom';

// Cartellino con codice a barre Code 128, in più formati (larghezza x altezza in mm).
// Il formato predefinito è 70 x 50 mm (70 di larghezza, 50 di altezza).
// Lo stesso componente <Cartellino> disegna sia l'anteprima (in scala) sia le pagine di stampa,
// quindi ciò che vedi è ciò che esce dalla stampante. Tutte le misure sono in mm.

/** Formati disponibili: larghezza x altezza in mm. */
export const FORMATI_CARTELLINO = [
  { id: '70x50', larghezza: 70, altezza: 50, label: '70 × 50 mm', nota: 'orizzontale' },
  { id: '50x70', larghezza: 50, altezza: 70, label: '50 × 70 mm', nota: 'verticale' },
  { id: '100x70', larghezza: 100, altezza: 70, label: '100 × 70 mm', nota: 'orizzontale' },
];
export const FORMATO_PREDEFINITO = FORMATI_CARTELLINO[0];

/** Formato dall'id (se sconosciuto: il predefinito). */
export const formatoCartellino = (id) => FORMATI_CARTELLINO.find((f) => f.id === id) ?? FORMATO_PREDEFINITO;

// ─── Adattamento del testo ──────────────────────────────────────────────────
// Nome ed etichetta si adattano allo spazio disponibile: il corpo del carattere scende (fino a un minimo
// leggibile) finché tutto il testo sta nel proprio riquadro. La stima usa il numero di caratteri
// (nessuna misura nel DOM), così anteprima e stampa danno lo stesso risultato anche con l'area di stampa nascosta.

const PADDING_VERT_MM = 2.5;          // sopra e sotto
const GAP_MM = 1;
const LARGHEZZA_CARATTERE = 0.66;     // larghezza media di una maiuscola in grassetto, in "em"
const INTERLINEA = 1.12;
const MIN_FONT_MM = 2.4;

/** Misure derivate dal formato: altezza del barcode, larghezza del testo, corpi massimi. */
const misure = (formato) => {
  const { larghezza: L, altezza: A } = formato;
  return {
    L,
    A,
    barcodeH: Math.max(17, Math.round(A * 0.34 * 10) / 10), // 50->17, 70->23.8
    larghezzaTesto: L - 5,                                 // meno i margini laterali del testo
    maxNome: Math.min(8, Math.max(4.4, L * 0.1)),
    maxEtichetta: Math.min(7, Math.max(3.9, L * 0.088)),
    corpoVoci: L > 60 ? 3.3 : 3,
  };
};

const righeStimate = (testo, fontMm, larghezzaMm) => {
  const perRiga = Math.max(1, Math.floor(larghezzaMm / (fontMm * LARGHEZZA_CARATTERE)));
  // le parole non si spezzano a metà: stima prudente sommando le parole riga per riga
  let righe = 1;
  let corrente = 0;
  String(testo ?? '').split(/\s+/).filter(Boolean).forEach((parola) => {
    const l = parola.length;
    if (l > perRiga) {
      righe += Math.ceil(l / perRiga) - (corrente === 0 ? 1 : 0);
      corrente = l % perRiga;
    } else if (corrente === 0) {
      corrente = l;
    } else if (corrente + 1 + l <= perRiga) {
      corrente += 1 + l;
    } else {
      righe += 1;
      corrente = l;
    }
  });
  return righe;
};

/** Corpo (mm) più grande, tra max e MIN_FONT_MM, per cui il testo sta in altezzaMm. */
const adattaTesto = (testo, altezzaMm, maxMm, larghezzaMm) => {
  for (let f = maxMm; f >= MIN_FONT_MM; f = Math.round((f - 0.1) * 10) / 10) {
    if (righeStimate(testo, f, larghezzaMm) * f * INTERLINEA <= altezzaMm) return f;
  }
  return MIN_FONT_MM;
};

/** Calcola corpo di nome ed etichetta dati i testi e le voci mostrate. */
const calcolaLayout = (dati, m) => {
  const voci = dati.voci || [];
  const blocchi = [dati.nome ? 'nome' : null, dati.etichetta ? 'etichetta' : null].filter(Boolean);
  // altezza delle voci (ognuna può andare a capo)
  const vociMm = voci.reduce((tot, v) => tot + righeStimate(v, m.corpoVoci, m.larghezzaTesto) * m.corpoVoci * 1.15 + 0.6, 0);
  const nBlocchi = 1 + blocchi.length + (voci.length ? 1 : 0);
  const libero = m.A - 2 * PADDING_VERT_MM - m.barcodeH - GAP_MM * (nBlocchi - 1) - vociMm;
  const disponibile = Math.max(libero, 8);

  // lo spazio si divide in proporzione alla lunghezza dei testi (ma ognuno ha almeno il 30%)
  const lung = { nome: String(dati.nome ?? '').length, etichetta: String(dati.etichetta ?? '').length };
  const tot = blocchi.reduce((t, b) => t + Math.max(lung[b], 1), 0);
  const quota = (b) => (blocchi.length === 1 ? 1 : Math.min(0.7, Math.max(0.3, Math.max(lung[b], 1) / tot)));

  return {
    nome: dati.nome ? adattaTesto(dati.nome, disponibile * quota('nome'), m.maxNome, m.larghezzaTesto) : null,
    etichetta: dati.etichetta ? adattaTesto(dati.etichetta, disponibile * quota('etichetta'), m.maxEtichetta, m.larghezzaTesto) : null,
  };
};

/**
 * dati: { nome: string|null, etichetta: string|null, voci: string[] }
 *   nome / etichetta: testi grandi che si adattano allo spazio (null = non mostrati);
 *   voci: righe piccole (SAP, ubicazione, scorta minima ...).
 * svg:  markup SVG del codice a barre (da useBarcodeCodice)
 */
export const Cartellino = ({ dati, svg, formato = FORMATO_PREDEFINITO }) => {
  const m = misure(formato);
  const layout = calcolaLayout(dati, m);
  return (
    <div
      className="cartellino"
      style={{
        width: `${m.L}mm`,
        height: `${m.A}mm`,
        boxSizing: 'border-box',
        padding: `${PADDING_VERT_MM}mm 1mm`,
        background: '#ffffff',
        color: '#000000',
        fontFamily: 'Arial, Helvetica, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        gap: `${GAP_MM}mm`,
        overflow: 'hidden',
      }}
    >
      {/* codice a barre: tutta la larghezza meno 1 mm di margine per lato */}
      <div
        style={{ flex: `0 0 ${m.barcodeH}mm`, height: `${m.barcodeH}mm`, width: `${m.L - 2}mm`, alignSelf: 'center', display: 'flex', justifyContent: 'center' }}
        dangerouslySetInnerHTML={{
          __html: svg
            ? svg.replace('<svg', '<svg style="width:100%;height:100%;display:block"')
            : '',
        }}
      />

      {/* nome articolo */}
      {dati.nome && (
        <div
          style={{
            fontSize: `${layout.nome}mm`,
            lineHeight: INTERLINEA,
            fontWeight: 800,
            textTransform: 'uppercase',
            wordBreak: 'break-word',
            flex: '0 0 auto',
            padding: '0 1.5mm',
          }}
        >
          {dati.nome}
        </div>
      )}

      {/* etichetta del componente (senza prefisso) */}
      {dati.etichetta && (
        <div
          style={{
            fontSize: `${layout.etichetta}mm`,
            lineHeight: INTERLINEA,
            fontWeight: 700,
            textTransform: 'uppercase',
            wordBreak: 'break-word',
            flex: '0 0 auto',
            padding: '0 1.5mm',
          }}
        >
          {dati.etichetta}
        </div>
      )}

      {/* voci facoltative */}
      {dati.voci.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.6mm',
            padding: '0 1.5mm',
            fontSize: `${m.corpoVoci}mm`,
            lineHeight: 1.15,
            flex: '0 0 auto',
          }}
        >
          {dati.voci.map((voce) => (
            <span key={voce} style={{ maxWidth: '100%', fontWeight: 600, wordBreak: 'break-word' }}>
              {voce}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/** Anteprima in scala: stesso markup della stampa, ingrandito con transform. */
export const CartellinoAnteprima = ({ dati, svg, scala = 1.4, formato = FORMATO_PREDEFINITO }) => (
  <div
    style={{ width: `${formato.larghezza * scala}mm`, height: `${formato.altezza * scala}mm`, maxWidth: '100%' }}
    className="shadow-xl border border-border/60 bg-white overflow-hidden"
  >
    <div style={{ transform: `scale(${scala})`, transformOrigin: 'top left', width: `${formato.larghezza}mm`, height: `${formato.altezza}mm` }}>
      <Cartellino dati={dati} svg={svg} formato={formato} />
    </div>
  </div>
);

const cssStampa = ({ larghezza, altezza }) => `
  #cartellini-stampa { display: none; }

  @media print {
    @page { size: ${larghezza}mm ${altezza}mm; margin: 0; }

    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
      overflow: visible !important;
      height: auto !important;
    }

    /* si stampano SOLO i cartellini: tutto il resto della pagina sparisce */
    body > *:not(#cartellini-stampa) { display: none !important; }

    #cartellini-stampa { display: block !important; }

    #cartellini-stampa .cartellino-pagina {
      width: ${larghezza}mm;
      height: ${altezza}mm;
      overflow: hidden;
      page-break-after: always;
      break-after: page;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    #cartellini-stampa .cartellino-pagina:last-child {
      page-break-after: auto;
      break-after: auto;
    }
  }
`;

/**
 * Area di stampa: un cartellino per pagina (interruzione di pagina tra le copie), con @page della misura scelta
 * e margini 0. Va montata solo mentre il modale è aperto; sullo schermo resta nascosta.
 */
export const AreaStampaCartellini = ({ dati, svg, copie, formato = FORMATO_PREDEFINITO }) => createPortal(
  <div id="cartellini-stampa" aria-hidden="true">
    <style>{cssStampa(formato)}</style>
    {Array.from({ length: Math.max(1, copie) }, (_, i) => (
      <div key={i} className="cartellino-pagina">
        <Cartellino dati={dati} svg={svg} formato={formato} />
      </div>
    ))}
  </div>,
  document.body,
);

/** Apre la finestra di stampa del browser (l'area di stampa dei cartellini deve essere montata). */
export const stampaCartellini = () => {
  window.print();
};
